import { useEffect, useRef, useState } from 'react';
import { Bar, Button, Dialog, FlexBox, MessageStrip, Text } from '@ui5/webcomponents-react';

/**
 * Barcode scanning for phones and tablets, using the browser's BarcodeDetector + camera — the same code path
 * on the web/PWA and inside the APK (its WebView is Chromium). No native plugin, so nothing to install on the
 * device and no extra APK weight.
 *
 * Desktop is deliberately not covered: warehouse USB/Bluetooth scanners act as keyboards, so they already type
 * straight into the search box or line field. The button hides itself where scanning isn't supported.
 */
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'code_93', 'itf', 'codabar', 'qr_code'];

interface DetectedBarcode { rawValue: string }
interface Detector { detect(source: CanvasImageSource): Promise<DetectedBarcode[]> }
declare global {
  interface Window { BarcodeDetector?: { new(options?: { formats?: string[] }): Detector; getSupportedFormats?(): Promise<string[]> } }
}

/** True when this device/browser can scan (camera + BarcodeDetector). */
export const scanSupported = (): boolean =>
  typeof window !== 'undefined' && typeof window.BarcodeDetector === 'function' && !!navigator.mediaDevices?.getUserMedia;

/** Camera button that reports the first barcode it reads. Renders nothing where scanning isn't supported. */
export function ScanButton({ onCode, tooltip = 'Scan barcode', design = 'Transparent', label }: {
  onCode(code: string): void; tooltip?: string; design?: 'Default' | 'Transparent' | 'Emphasized'; label?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!scanSupported()) return null;
  return (
    <>
      <Button icon="bar-code" design={design} tooltip={tooltip} accessibleName={tooltip} onClick={() => setOpen(true)}>{label}</Button>
      {open && <ScannerDialog onClose={() => setOpen(false)} onCode={(c) => { setOpen(false); onCode(c); }} />}
    </>
  );
}

function ScannerDialog({ onCode, onClose }: { onCode(code: string): void; onClose(): void }) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | undefined, timer: number | undefined, stopped = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } });
        if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
        if (video.current) { video.current.srcObject = stream; await video.current.play().catch(() => {}); }
        const detector = new window.BarcodeDetector!({ formats: FORMATS });
        const tick = async () => {
          if (stopped || !video.current || video.current.readyState < 2) { timer = window.setTimeout(tick, 150); return; }
          try {
            const [hit] = await detector.detect(video.current);
            if (hit?.rawValue) { navigator.vibrate?.(60); onCode(hit.rawValue.trim()); return; }
          } catch { /* a frame that can't be decoded is normal — keep going */ }
          timer = window.setTimeout(tick, 150);
        };
        tick();
      } catch (e) {
        const err = e as Error;
        setError(err.name === 'NotAllowedError'
          ? 'Camera permission was refused. Allow camera access for B1Links, then try again.'
          : `Camera unavailable: ${err.message}`);
      }
    })();
    return () => { stopped = true; if (timer) clearTimeout(timer); stream?.getTracks().forEach(t => t.stop()); };
  }, [onCode]);

  return (
    <Dialog open headerText="Scan barcode" style={{ width: 'min(30rem, 96vw)' }} onClose={onClose}
      footer={<Bar endContent={<Button onClick={onClose}>Cancel</Button>} />}>
      <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
        {error
          ? <MessageStrip design="Negative" hideCloseButton>{error}</MessageStrip>
          : <>
            {/* muted + playsInline so the stream starts without a user gesture on iOS/Android */}
            <video ref={video} muted playsInline style={{ width: '100%', borderRadius: '0.5rem', background: '#000', aspectRatio: '4 / 3', objectFit: 'cover' }} />
            <Text style={{ color: 'var(--sapNeutralTextColor)' }}>Point the camera at the barcode.</Text>
          </>}
      </FlexBox>
    </Dialog>
  );
}
